import { useState, useEffect } from 'react';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { useNavigate, useLocation } from 'react-router-dom';
import { isAxiosError } from 'axios';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import './AchievementsSegment.css';

export interface IAchievement {
  idOsiagniecia: number;
  nazwa: string;
}

const AchievementsSegment = () => {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();

  const [achievements, setAchievements] = useState<IAchievement[]>([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState({ nazwa: '' });
  const [loading, setLoading] = useState(false);

  // Downloading achievements when installing a component
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchAchievements = async () => {
      try {
        const response = await axiosPrivate.get('/achievements', {
          signal: controller.signal,
        });
        if (isMounted) {
          setAchievements(response.data);
        }
      } catch (error) {
        if (isAxiosError(error)) {
          if (error.response?.status === 403) {
            navigate('/', { state: { from: location }, replace: true });
          } else {
            console.error('Error fetching achievements:', error);
          }
        }
      }
    };

    fetchAchievements();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [axiosPrivate, navigate, location]);

  const handleOpenAddDialog = () => {
    setFormData({ nazwa: '' });
    setOpenAddDialog(true);
  };

  const handleOpenEditDialog = (achievement: IAchievement) => {
    setEditingId(achievement.idOsiagniecia);
    setFormData({ nazwa: achievement.nazwa });
    setOpenEditDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setFormData({ nazwa: '' });
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingId(null);
    setFormData({ nazwa: '' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddAchievement = async () => {
    if (!formData.nazwa) {
      alert('Proszę wypełnić pole nazwy');
      return;
    }

    setLoading(true);
    try {
      const response = await axiosPrivate.post('/achievements', formData);
      const newAchievement: IAchievement = {
        idOsiagniecia: response.data.id,
        nazwa: formData.nazwa,
      };
      setAchievements([newAchievement, ...achievements]);
      handleCloseAddDialog();
      alert('Osiągnięcie dodane pomyślnie');
    } catch (error) {
      if (isAxiosError(error)) {
        console.error('Error adding achievement:', error);
        alert('Błąd podczas dodawania osiągnięcia');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateAchievement = async () => {
    if (!formData.nazwa) {
      alert('Proszę wypełnić pole nazwy');
      return;
    }

    if (!editingId) return;

    setLoading(true);
    try {
      await axiosPrivate.put(`/achievements/${editingId}`, formData);
      setAchievements(
        achievements.map((a) =>
          a.idOsiagniecia === editingId
            ? { ...a, nazwa: formData.nazwa }
            : a
        )
      );
      handleCloseEditDialog();
      alert('Osiągnięcie zaktualizowane pomyślnie');
    } catch (error) {
      if (isAxiosError(error)) {
        console.error('Error updating achievement:', error);
        alert('Błąd podczas aktualizacji osiągnięcia');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAchievement = async (id: number) => {
    if (!window.confirm('Czy na pewno chcesz usunąć to osiągnięcie?')) {
      return;
    }

    setLoading(true);
    try {
      await axiosPrivate.delete(`/achievements/${id}`);
      setAchievements(achievements.filter((a) => a.idOsiagniecia !== id));
      alert('Osiągnięcie usunięte pomyślnie');
    } catch (error) {
      if (isAxiosError(error)) {
        console.error('Error deleting achievement:', error);
        alert('Błąd podczas usuwania osiągnięcia');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="achievements-segment" >
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpenAddDialog}
        sx={{ marginBottom: '20px' }}>
        Dodaj nowe osiągnięcie
      </Button>

      <TableContainer component={Paper}
      sx={{minHeight:550,maxHeight:550}}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5'}}>
              <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nazwa</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {achievements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  Brak osiągnięć
                </TableCell>
              </TableRow>
            ) : (
              achievements.map((achievement) => (
                <TableRow key={achievement.idOsiagniecia}>
                  <TableCell>{achievement.idOsiagniecia}</TableCell>
                  <TableCell>{achievement.nazwa}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenEditDialog(achievement)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteAchievement(achievement.idOsiagniecia)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog for add new achievement */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Dodaj nowe osiągnięcie</DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '16px' }}>
            <TextField
              label="Nazwa"
              name="nazwa"
              value={formData.nazwa}
              onChange={handleInputChange}
              fullWidth
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog}>Anuluj</Button>
          <Button onClick={handleAddAchievement} variant="contained" disabled={loading}>
            {loading ? 'Dodawanie...' : 'Dodaj'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog for edit achievement */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edytuj osiągnięcie</DialogTitle>
        <DialogContent>
          <div >
            <TextField
              label="Nazwa"
              name="nazwa"
              value={formData.nazwa}
              onChange={handleInputChange}
              fullWidth
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Anuluj</Button>
          <Button onClick={handleUpdateAchievement} variant="contained" disabled={loading}>
            {loading ? 'Aktualizowanie...' : 'Zaktualizuj'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AchievementsSegment;
